import {
  BlogSettingsChanges,
  BlogSettingsSave,
  ConnectionInfoResponse,
  Endpoint,
} from "../../typings/types"
import {
  BlockStack,
  Button,
  FormLayout,
  Grid,
  InlineStack,
  List,
  Modal,
  Select,
  Text,
  TextField,
} from "@shopify/polaris"
import { useCallback, useEffect, useState } from "react"

import { useAppBridge } from "@shopify/app-bridge-react"
import { useTranslation } from "react-i18next"
import { userLoggedInFetch } from "../App"

const BlogSettings = ({
  active,
  name,
  handleClose,
  handleDisconnect,
  forceSync,
}: {
  active?: string
  name?: string
  handleClose: (arg0?: BlogSettingsSave) => void
  handleDisconnect?: () => void
  forceSync: () => void
}) => {
  const { t } = useTranslation(["common", "home"])
  const app = useAppBridge()
  const fetch = userLoggedInFetch(app)
  const [loading, setLoading] = useState(false)
  const [blogData, setBlogData] = useState<ConnectionInfoResponse>()
  const [blogsInfo, setBlogsInfo] = useState<BlogSettingsChanges>({})
  const [ignoredTags, setIgnoredTags] = useState("")

  // const handleSelectChange = useCallback(
  //   (childId: string, parentId: string) => {
  //     const existingConnection = blogData?.blog_connections.find(
  //       ({ parent_id }) => parent_id === parentId
  //     )

  //     // if the connection doesn't exist (the parent blog hasn't been synced yet)
  //     // or the connection does exist but is currently connected to a different blog
  //     if (!existingConnection || existingConnection.child_id !== childId) {
  //       setBlogsInfo((prev) => ({
  //         ...prev,
  //         [parentId]: {
  //           newInfo: childId,
  //           oldInfo: existingConnection?.child_id,
  //         },
  //       }))
  //       return
  //     } else if (
  //       !!existingConnection &&
  //       existingConnection.child_id === childId
  //     )
  //       setBlogsInfo((prev) => {
  //         const reset = { ...prev }
  //         delete reset[parentId]
  //         return reset
  //       })
  //   },
  //   [blogData]
  // )

  useEffect(() => {
    if (!!active) {
      setLoading(true)
      fetch(Endpoint.CONNECTION + `?domain=${active}`) // LINK server/index.ts#get-connection
        .then((res) => {
          if (!!res && !res.ok) {
            handleClose()
            return
          }
          return res?.json()
        })
        .then((res: ConnectionInfoResponse) => {
          if (!res) return
          setBlogData(res)
          setIgnoredTags(res.ignored_tags || "")
          setBlogsInfo(() => {
            const info = {} as BlogSettingsChanges
            const { parent_blogs, blog_connections } = res
            for (const { id } of parent_blogs) {
              const existingConnection = blog_connections.find(
                ({ parent_id }) => parent_id === id
              )
              info[id] = {
                oldInfo: {
                  blog: !!existingConnection
                    ? existingConnection.child_id ||
                      (existingConnection.pending ? "new" : "none")
                    : "new",
                  date: existingConnection?.sync_after,
                },
              }
              if (!existingConnection) {
                info[id].newInfo = { blog: "new" }
              }
            }
            return info
          })
          setLoading(false)
        })
    }
  }, [active])

  const handleChange = (
    domain: string,
    field: "date" | "blog",
    value: string
  ) => {
    setBlogsInfo((prev) => {
      const info = { ...prev }
      const prevValue = info[domain]?.oldInfo[field]
      if (
        field === "date" &&
        value.length < 1 &&
        !!info[domain].newInfo?.date &&
        !info[domain].oldInfo?.date
      )
        // date is cleared and there was previously a new date selected and the date was originally blank
        delete info[domain].newInfo?.date
      else {
        if (prevValue !== value) {
          // this is a new value
          info[domain].newInfo = { ...info[domain].newInfo, [field]: value }
        } else if (info[domain].newInfo?.[field]) {
          delete info[domain].newInfo?.[field]
        }
      }
      return info
    })
  }

  const noDatesSet = true

  const ignoredTagsChanged = ignoredTags !== (blogData?.ignored_tags || "")
  const blogsChanged = Object.values(blogsInfo).some(
    ({ newInfo }) => !!newInfo && Object.keys(newInfo).length > 0
  )

  return (
    <Modal
      loading={loading}
      open={!!active}
      onClose={() => handleClose()}
      sectioned={true}
      title={
        loading
          ? t("home:blogSettings.loading")
          : t("home:initialSync.settings.action")
      }
      primaryAction={{
        content: t("common:save"),
        onAction: () =>
          handleClose({
            blogs: blogsInfo,
            ...(ignoredTagsChanged ? { ignored_tags: ignoredTags } : {}),
          }),
        disabled: !blogsChanged && !ignoredTagsChanged,
      }}
      secondaryActions={[
        {
          content: t("common:cancel"),
          onAction: () => handleClose(),
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {t("home:blogSettings.description")}
          <Text variant="headingMd" as="h3">
            {t("home:blogSettings.blogSelection")}
          </Text>
          <List>
            <List.Item>
              {t("home:blogSettings.bullet1")}
              <br />
              <Text tone="subdued" as="span">
                {t("home:blogSettings.bullet1Description")}
              </Text>
            </List.Item>
            <List.Item>
              <code>[{t("home:blogSettings.options.new")}]</code>{" "}
              {t("home:blogSettings.bullet2")}
            </List.Item>
            <List.Item>
              <code>[{t("home:blogSettings.options.dontSync")}]</code>{" "}
              {t("home:blogSettings.bullet3")}
            </List.Item>
          </List>
          <Text variant="headingMd" as="h3">
            {t("home:blogSettings.syncAfter")}
          </Text>
          <p>{t("home:blogSettings.syncAfterDescription")}</p>
          <InlineStack gap="400" align="space-between">
            <div>
              <Text tone="subdued" as="span">{`Parent Blog ->`}</Text> Child
              Blog Handle
            </div>
            <div>Sync After</div>
          </InlineStack>
        </BlockStack>

        {blogData?.parent_blogs?.map(({ id, title }) => {
          const { blog_connections, child_blogs } = blogData
          const existingConnection = blog_connections.find(
            ({ parent_id }) => parent_id === id
          )

          return (
            <Grid
              columns={{ xs: 1, sm: 3, md: 3, lg: 3, xl: 3 }}
              areas={{
                xs: ["name", "date"],
                sm: ["name name date"],
                md: ["name name date"],
                lg: ["name name date"],
                xl: ["name name date"],
              }}
            >
              <Grid.Cell area="name">
                <Select
                  label={`${title} ->`}
                  labelInline
                  options={[
                    { label: "[Create New]", value: "new" },
                    ...child_blogs.map((b) => ({
                      label: `/${b.handle}`,
                      value: b.id,
                    })),
                    { label: "[Don't Sync]", value: "none" },
                  ]}
                  onChange={(value) => handleChange(id, "blog", value)}
                  value={
                    blogsInfo[id]?.newInfo?.blog || // the selected value
                    (existingConnection &&
                      (existingConnection.child_id ||
                        (existingConnection.pending ? "new" : "none"))) || // the existing connection
                    "new"
                  }
                />
              </Grid.Cell>
              <Grid.Cell area="date">
                <TextField
                  label={null}
                  type="date"
                  disabled={
                    (blogsInfo[id] && blogsInfo[id].newInfo?.blog === "none") ||
                    (blogsInfo[id] &&
                      !blogsInfo[id].newInfo?.blog &&
                      existingConnection &&
                      !existingConnection.child_id &&
                      !existingConnection.pending)
                  }
                  value={
                    typeof blogsInfo[id]?.newInfo?.date === "string"
                      ? (blogsInfo[id].newInfo?.date as string).length > 0
                        ? blogsInfo[id]?.newInfo?.date
                        : undefined
                      : existingConnection?.sync_after || undefined
                  }
                  onChange={(value) => handleChange(id, "date", value)}
                  autoComplete="off"
                  align="right"
                />
              </Grid.Cell>
            </Grid>
          )
        })}
      </Modal.Section>
      <Modal.Section>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h3">
            {t("home:blogSettings.ignoredTags")}
          </Text>
          <p>{t("home:blogSettings.ignoredTagsDescription")}</p>
          <TextField
            label={t("home:blogSettings.ignoredTags")}
            labelHidden
            multiline={4}
            value={ignoredTags}
            onChange={setIgnoredTags}
            placeholder={t("home:blogSettings.ignoredTagsPlaceholder")}
            helpText={t("home:blogSettings.ignoredTagsHelp")}
            autoComplete="off"
          />
        </BlockStack>
      </Modal.Section>
      <Modal.Section>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h3">
            {t("home:blogSettings.forceSection.button")}
          </Text>
          <p>{t("home:blogSettings.forceSection.description")}</p>
          <Button
            variant="secondary"
            tone="critical"
            size="slim"
            onClick={forceSync}
          >
            {t("home:blogSettings.forceSection.button")}
          </Button>
          <Text variant="headingMd" as="h3">
            {t("home:blogSettings.disconnectSection.header")}
          </Text>
          <Button variant="plain" tone="critical" onClick={handleDisconnect}>
            {t("home:blogSettings.disconnectSection.button", {
              domain: name || active || t("home:blogSettings.thisShop"),
            }) || ""}
          </Button>
        </BlockStack>
      </Modal.Section>
    </Modal>
  )
}

export default BlogSettings
